// src/pages/conversaciones/components/ConversacionFormModal.tsx
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { useCrearConversacion } from '@/hooks/queries/useConversaciones';
import { useClientes } from '@/hooks/queries/useClientes';
import { useNavigate } from 'react-router-dom';

const schema = z.object({
  cliente_id: z.string().min(1, 'Seleccione un cliente'),
  asunto: z.string().optional(),
  mensaje_inicial: z.string().min(1, 'Escriba un mensaje inicial'),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ConversacionFormModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const crearMutation = useCrearConversacion();
  const { data: clientesData } = useClientes({ page: 1, page_size: 100 });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      cliente_id: '',
      asunto: '',
      mensaje_inicial: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      const conversacion = await crearMutation.mutateAsync(data);
      form.reset();
      onClose();
      navigate(`/conversaciones/${conversacion.id}`);
    } catch {
      // Error manejado por el hook
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva Conversacion</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cliente_id">
              Cliente <span className="text-red-500">*</span>
            </Label>
            <Select
              value={form.watch('cliente_id')}
              onValueChange={(value) => form.setValue('cliente_id', value)}
            >
              <SelectTrigger error={!!form.formState.errors.cliente_id}>
                <SelectValue placeholder="Seleccione un cliente" />
              </SelectTrigger>
              <SelectContent>
                {clientesData?.items?.map((cliente) => (
                  <SelectItem key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.cliente_id && (
              <p className="text-sm text-red-500">
                {form.formState.errors.cliente_id.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="asunto">Asunto</Label>
            <Input
              id="asunto"
              {...form.register('asunto')}
              placeholder="Tema de la conversacion..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mensaje_inicial">
              Mensaje Inicial <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="mensaje_inicial"
              {...form.register('mensaje_inicial')}
              placeholder="Escriba el primer mensaje..."
              rows={4}
            />
            {form.formState.errors.mensaje_inicial && (
              <p className="text-sm text-red-500">
                {form.formState.errors.mensaje_inicial.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={crearMutation.isPending}>
              Iniciar Conversacion
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
